import { useEffect, useMemo, useReducer, useState } from 'react'
import { TablePaginationConfig, message, Modal } from 'antd'
import { useRecoilValue } from 'recoil'
import { zonedTimeToUtc } from 'date-fns-tz'

import vendorAtom from '@src/atom/vendor'
import { useCopyCompanyChallengeMutate } from '@src/hooks/queries/challenge/useCopyCompanyChallengeMutate'
import { useGetCompanyChallengesQuery } from '@src/hooks/queries/challenge/useGetCompanyChallengesQuery'
import { CompanyChallengeDto, GetCompanyChallengesQueryKey } from '@src/type/challenge'
import { useDeleteCompanyChallengeMutate } from '@src/hooks/queries/challenge/useDeleteCompanyChallengeMutate'
import { getDateByTimezone } from '@src/utils/utils'
import {
	CompanyChallengeTableItem,
	CompanyChallengeTableItemValue,
	TableItemValuesAction,
} from '../type'
import { useUpdateCompanyChallengeStatusMutate } from '@src/hooks/queries/challenge/useUpdateCompanyChallengeStatusMutate'

const { confirm } = Modal

type Param = {
	selectedTableItemIndex: number | undefined
	onChallengePreviewOpened: (index: number | undefined) => void
	onModificationOpened: (index: number | undefined) => Promise<void>
}

export function useHotdealChallengeTable({
	selectedTableItemIndex,
	onChallengePreviewOpened,
	onModificationOpened,
}: Param) {
	const vendor = useRecoilValue(vendorAtom)

	const [pagination, setPagination] = useState<TablePaginationConfig>({
		position: ['bottomCenter'],
		showSizeChanger: false,
		current: 1,
		pageSize: 10,
		onChange(page) {
			setPagination((prevPagination) => ({ ...prevPagination, current: page }))
		},
	})

	const queryKey = useMemo<GetCompanyChallengesQueryKey>(() => {
		const parsedVendorNo = Number.parseInt(vendor.no, 10)
		const vendorNo = Number.isNaN(parsedVendorNo) ? undefined : parsedVendorNo

		return ['getCompanyChallenges', vendorNo, pagination.current]
	}, [vendor, pagination])

	const { data: resBody, isFetching: isFetchingCompanyChallenges } = useGetCompanyChallengesQuery({
		queryKey,
	})

	const { mutateAsync: copyCompanyChallengeMutate } = useCopyCompanyChallengeMutate({
		invalidateQueryKey: queryKey,
	})

	const { mutateAsync: deleteCompanyChallengeMutate } = useDeleteCompanyChallengeMutate({
		invalidateQueryKey: queryKey,
	})

	const { mutate: updateCompanyChallengeStatusMutate } = useUpdateCompanyChallengeStatusMutate({
		invalidateQueryKey: queryKey,
	})

	const [tableItemValues, tableItemValuesDispatch] = useReducer(tableItemValuesReducer, undefined)

	useEffect(() => {
		if (resBody === undefined) {
			return
		}

		setPagination((prevPagination) => ({
			...prevPagination,
			total: resBody.data.meta.total_count,
		}))

		tableItemValuesDispatch({ type: 'set', challenges: resBody.data.items })
	}, [resBody])

	const tableItems = useMemo<CompanyChallengeTableItem[] | undefined>(() => {
		return tableItemValues?.map((tableItemValue, index) => ({
			...tableItemValue,
			onClickPreview() {
				onChallengePreviewOpened(index)
			},
			async onClickLink() {
				const link = `https://motiners.com/campaign/${tableItemValue.challengeId || 'null'}`

				await navigator.clipboard.writeText(link)

				await message.success('링크가 복사되었어요')
			},
			async onClickCopy() {
				tableItemValuesDispatch({ type: 'startCopy', index })

				try {
					await copyCompanyChallengeMutate(tableItemValue.id)
					tableItemValuesDispatch({ type: 'endCopy', index })
					await message.success('챌린지를 복제했어요')
				} catch (e) {
					console.log(e)
					tableItemValuesDispatch({ type: 'endCopy', index })
					await message.error('챌린지 복제 실패')
				}
			},
			async onClickModify() {
				await onModificationOpened(index)
			},
			onClickDelete() {
				confirm({
					centered: true,
					title: '챌린지를 삭제할까요?',
					icon: null,
					async onOk() {
						try {
							await deleteCompanyChallengeMutate(tableItemValue.id)
							tableItemValuesDispatch({ type: 'endDelete', index })
							// eslint-disable-next-line @typescript-eslint/no-floating-promises
							message.success('삭제 되었어요')
						} catch (e) {
							console.log(e)
							tableItemValuesDispatch({ type: 'endDelete', index })
							// eslint-disable-next-line @typescript-eslint/no-floating-promises
							message.error('챌린지 삭제 실패')
						}
					},
					okText: '삭제',
					cancelText: '취소',
					okButtonProps: {
						danger: true,
					},
				})
			},
			onChangeExposure(checked: boolean) {
				if (!tableItemValue.id) return

				updateCompanyChallengeStatusMutate({
					companyChallengeNo: String(tableItemValue.id),
					status: checked ? 'private' : 'pending',
				})
			},
		}))
	}, [
		tableItemValues,
		onChallengePreviewOpened,
		copyCompanyChallengeMutate,
		onModificationOpened,
		deleteCompanyChallengeMutate,
		updateCompanyChallengeStatusMutate,
	])

	const selectedTableItem = useMemo<CompanyChallengeTableItem | undefined>(() => {
		if (selectedTableItemIndex === undefined || tableItems === undefined) {
			return undefined
		}

		return tableItems.at(selectedTableItemIndex)
	}, [selectedTableItemIndex, tableItems])

	return {
		pagination,
		isFetchingCompanyChallenges,
		tableItems,
		queryKey,
		selectedTableItem,
	}
}

function tableItemValuesReducer(
	prevTableItemValues: CompanyChallengeTableItemValue[] | undefined,
	action: TableItemValuesAction,
): CompanyChallengeTableItemValue[] | undefined {
	switch (action.type) {
		case 'set': {
			const { challenges } = action

			return challenges.map(dtoToTableItemValue)
		}

		case 'startCopy': {
			if (prevTableItemValues === undefined) {
				return undefined
			}

			const { index } = action

			return [
				...prevTableItemValues.slice(0, index),
				{
					...prevTableItemValues[index],
					isCopying: true,
				},
				...prevTableItemValues.slice(index + 1),
			]
		}

		case 'endCopy': {
			if (prevTableItemValues === undefined) {
				return undefined
			}

			const { index } = action

			return [
				...prevTableItemValues.slice(0, index),
				{
					...prevTableItemValues[index],
					isCopying: false,
				},
				...prevTableItemValues.slice(index + 1),
			]
		}

		case 'startModify': {
			if (prevTableItemValues === undefined) {
				return undefined
			}

			const { index } = action

			return [
				...prevTableItemValues.slice(0, index),
				{
					...prevTableItemValues[index],
					isModifying: true,
				},
				...prevTableItemValues.slice(index + 1),
			]
		}

		case 'endModify': {
			if (prevTableItemValues === undefined) {
				return undefined
			}

			const { index } = action

			return [
				...prevTableItemValues.slice(0, index),
				{
					...prevTableItemValues[index],
					isModifying: false,
				},
				...prevTableItemValues.slice(index + 1),
			]
		}

		case 'startDelete': {
			if (prevTableItemValues === undefined) {
				return undefined
			}

			const { index } = action

			return [
				...prevTableItemValues.slice(0, index),
				{
					...prevTableItemValues[index],
					isDeleting: true,
				},
				...prevTableItemValues.slice(index + 1),
			]
		}

		case 'endDelete': {
			if (prevTableItemValues === undefined) {
				return undefined
			}

			const { index } = action

			return [
				...prevTableItemValues.slice(0, index),
				{
					...prevTableItemValues[index],
					isDeleting: false,
				},
				...prevTableItemValues.slice(index + 1),
			]
		}

		default: {
			throw new Error(`unknown type`)
		}
	}
}

function dtoToTableItemValue(dto: CompanyChallengeDto): CompanyChallengeTableItemValue {
	const now = new Date()
	const today = getDateByTimezone('Asia/Seoul', 'yyyy-MM-dd', now)

	let exposureStatus

	if (dto.status !== 'approved') {
		exposureStatus = '노출 안함'
	} else if (dto.end_date === null || dto.exposure_date === null) {
		console.log('노출 승인이 되었지만 챌린지 종료일 또는 모집 시작일이 없음')
		exposureStatus = 'undefined'
	} else if (today > dto.end_date) {
		exposureStatus = '종료'
	} else if (today < dto.exposure_date) {
		exposureStatus = '노출 예정'
	} else {
		exposureStatus = '노출'
	}

	return {
		id: Number.parseInt(dto.no, 10),
		status: dto.status,
		isTemp: dto.is_temporary,
		challengeId: dto.challenge_no !== null ? Number.parseInt(dto.challenge_no, 10) : null,
		challengeName: dto.title,
		challengeCoverImage: dto.cover_img,
		challengeBannerImage: dto.banner_img,
		exposureStartDate: dto.exposure_date ? zonedTimeToUtc(dto.exposure_date, 'Asia/Seoul') : null,
		certificationStartDate: dto.start_date ? zonedTimeToUtc(dto.start_date, 'Asia/Seoul') : null,
		certificationEndDate: dto.end_date ? zonedTimeToUtc(dto.end_date, 'Asia/Seoul') : null,
		certificationExampleImages: dto.sample_proof,
		taskName: dto.task_name,
		hotdealItemImage: dto.product_img,
		hotdealItemName: dto.product_name,
		hotdealItemLink: dto.product_link,
		beforePrice: typeof dto.price === 'string' ? Number.parseInt(dto.price, 10) : dto.price,
		afterPrice:
			typeof dto.discount_price === 'string'
				? Number.parseInt(dto.discount_price, 10)
				: dto.discount_price,
		descriptionImages: dto.detail_descriptions,
		canLink: dto.status === 'approved',
		canPreview: !dto.is_temporary,
		canCopy: !dto.is_temporary,
		canModify: dto.is_temporary || (dto.exposure_date ? dto.exposure_date > today : false),
		canDelete: dto.is_temporary || (dto.exposure_date ? dto.exposure_date > today : false),
		exposureStatus,
		isCopying: false,
		isModifying: false,
		isDeleting: false,
		isExposure: dto.status !== 'pending', // 기업 페이지 노출 여부
	}
}
